import * as Sentry from '@sentry/nuxt';
import type { LogContext, LogCategory } from './logger.types';

const isDev = process.env.NODE_ENV !== 'production';

const styles: Record<string, string> = {
  debug: '\x1b[90m',
  info: '\x1b[36m',
  warn: '\x1b[33m',
  error: '\x1b[31m',
};

function normalizeError(error: unknown): Error | undefined {
  if (!error) return undefined;

  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'object' && error !== null) {
    const err = error as Record<string, unknown>;
    if (err.message || err.code) {
      const e = new Error(String(err.message || err.code));
      e.stack = String(err.stack || '');
      return e;
    }
  }

  return new Error(String(error));
}

function logToConsole(
  level: 'debug' | 'info' | 'warn' | 'error',
  message: string,
  context?: LogContext,
  category?: LogCategory,
) {
  if (!isDev && process.server) return;

  const reset = '\x1b[0m';
  const color = styles[level] || '';
  const categoryLabel = category ? `[${category}]` : '';
  const metaStr = context ? JSON.stringify(context, null, 2) : '';

  if (process.server) {
    console[level](
      `${level.toUpperCase()}${categoryLabel}: ${message} ${metaStr}`,
    );
  } else {
    console[level](
      `${color}${level.toUpperCase()}${reset}${categoryLabel}: ${message} ${metaStr}`,
    );
  }
}

function sendToSentry(
  level: 'info' | 'warning' | 'error',
  message: string,
  context?: LogContext,
  error?: Error,
  category?: LogCategory,
) {
  const tags = { category: category || 'application' };

  if (level === 'error') {
    if (error) {
      Sentry.captureException(error, { level, extra: context, tags });
    } else {
      Sentry.captureMessage(message, { level, extra: context, tags });
    }
  } else if (level === 'warning') {
    Sentry.captureMessage(message, { level, extra: context, tags });
  } else if (
    level === 'info' &&
    (context as { shouldReport?: boolean })?.shouldReport
  ) {
    Sentry.captureMessage(message, { level, extra: context, tags });
  }
}

export const logger = {
  debug(message: string, context?: LogContext, category?: LogCategory) {
    logToConsole('debug', message, context, category);
  },

  info(
    message: string,
    context?: LogContext,
    shouldReport = false,
    category?: LogCategory,
  ) {
    logToConsole('info', message, context, category);
    if (shouldReport) {
      sendToSentry('info', message, context, undefined, category);
    }
  },

  warn(message: string, context?: LogContext, category?: LogCategory) {
    logToConsole('warn', message, context, category);
    sendToSentry('warning', message, context, undefined, category);
  },

  error(
    message: string,
    error?: unknown,
    context?: LogContext,
    category?: LogCategory,
  ) {
    const errorObj = normalizeError(error);
    const errorContext = errorObj
      ? {
          ...context,
          error: { message: errorObj.message, stack: errorObj.stack },
        }
      : context;
    logToConsole('error', message, errorContext, category);
    sendToSentry('error', message, errorContext, errorObj, category);
  },
};

export type Logger = typeof logger;
